import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface SubscriptionData {
  active: number;
  trial: number;
  churned: number;
  total: number;
}

interface AdminTenant {
  id: string;
  subscription_status?: string | null;
  status: string;
}

interface AdminTenantsResponse {
  tenants: AdminTenant[];
  total: number;
}

function SubscriptionsOverviewWidget({ context }: WidgetProps) {
  const [data, setData] = useState<SubscriptionData | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);

  useEffect(() => {
    if (!context.isDirector) return;

    void engineFetchJson<AdminTenantsResponse>('/admin/tenants?limit=200')
      .then((response) => {
        const tenants = response.tenants ?? [];
        const active = tenants.filter((tenant) => tenant.subscription_status === 'active').length;
        const trial = tenants.filter(
          (tenant) => tenant.subscription_status === 'trial' || tenant.subscription_status === 'trialing',
        ).length;
        const churned = tenants.filter((tenant) =>
          ['cancelled', 'canceled', 'churned', 'suspended'].includes(
            (tenant.subscription_status || tenant.status || '').toLowerCase(),
          ),
        ).length;
        setData({
          active,
          trial,
          churned,
          total: response.total ?? tenants.length,
        });
        setEndpointMissing(false);
      })
      .catch(() => {
        setEndpointMissing(true);
      });
  }, [context.authToken, context.isDirector]);

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Subscriptions Overview</span>
        <a href="/command" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Details
        </a>
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 12px' }}>
        {endpointMissing ? (
          <div>
            <div style={{ color: 'var(--sp-warn)', fontSize: 12, marginBottom: 4 }}>
              Awaiting engine endpoint
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginBottom: 8 }}>
              GET /api/v1/admin/tenants
            </div>
            {/* Mock subscription cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, opacity: 0.35 }}>
              {[
                { label: 'Active', value: '--', color: 'var(--sp-ok)' },
                { label: 'Trial', value: '--', color: 'var(--sp-warn)' },
                { label: 'Churned', value: '--', color: 'var(--sp-error)' },
                { label: 'Total', value: '--', color: 'var(--sp-accent)' },
              ].map((c) => (
                <div
                  key={c.label}
                  style={{
                    padding: 8,
                    background: 'var(--sp-bg)',
                    borderRadius: 4,
                    border: '1px solid var(--sp-border)',
                  }}
                >
                  <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                    {c.label}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{c.value}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginTop: 6 }}>
              Will show director-facing subscription counts when the admin tenant list is available.
            </div>
          </div>
        ) : data ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              <div style={{ padding: 8, background: 'var(--sp-bg)', borderRadius: 4, border: '1px solid var(--sp-border)' }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>Active</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--sp-ok)' }}>{data.active}</div>
              </div>
              <div style={{ padding: 8, background: 'var(--sp-bg)', borderRadius: 4, border: '1px solid var(--sp-border)' }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>Trial</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--sp-warn)' }}>{data.trial}</div>
              </div>
              <div style={{ padding: 8, background: 'var(--sp-bg)', borderRadius: 4, border: '1px solid var(--sp-border)' }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>Churned</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--sp-error)' }}>{data.churned}</div>
              </div>
              <div style={{ padding: 8, background: 'var(--sp-bg)', borderRadius: 4, border: '1px solid var(--sp-border)' }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>Total</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--sp-accent)' }}>{data.total}</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--sp-text-dim)', marginTop: 8 }}>
              Tenant counts only — revenue figures are not yet surfaced in this widget.
            </div>
          </div>
        ) : (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>Loading subscription data...</div>
        )}
      </div>
    </div>
  );
}

export const subscriptionsOverviewWidget: WidgetDefinition = {
  id: 'subscriptions-overview',
  name: 'Subscriptions Overview',
  description: 'Active, trial, and churned subscription counts with MRR.',
  icon: 'credit-card',
  category: 'admin',
  scope: 'director',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 4, h: 3 },
  component: SubscriptionsOverviewWidget,
};
